import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { PortalLayout } from "@/pages/Portal";
import { AttachmentList } from "@/components/wp/QueryPanel";
import { uploadFile, deleteUpload } from "@/lib/upload";
import { ChevronLeft, Plus, UploadCloud, FileText, CheckCircle2, Clock, Paperclip, X, Send, AlertTriangle, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CLIENT_STATUS = {
    draft: { label: "Draft — not sent", cls: "bg-slate-100 text-slate-600", icon: Clock },
    submitted: { label: "Sent to Qube", cls: "bg-blue-100 text-blue-800", icon: CheckCircle2 },
    being_checked: { label: "Being checked", cls: "bg-amber-100 text-amber-800", icon: Clock },
    needs_info: { label: "More information needed", cls: "bg-rose-100 text-rose-800", icon: AlertTriangle },
    accepted: { label: "Accepted", cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
};
// Client never sees internal mechanics (collated/linked) — those map to friendly client statuses.
function clientStatusKey(s) {
    if (s.status === "draft") return "draft";
    if (s.client_status && CLIENT_STATUS[s.client_status]) return s.client_status;
    return ({ submitted: "submitted", received: "being_checked", collated: "being_checked", linked: "accepted" })[s.status] || "submitted";
}

// Quick-answer response model for document requests.
const DOC_STATUS = [
    { key: "attached", label: "I'll attach it" },
    { key: "already_provided", label: "Already provided" },
    { key: "not_available", label: "Not available yet" },
    { key: "not_applicable", label: "Not applicable" },
];

function effStatus(a) {
    a = a || {};
    return a.status || ((a.attachments || []).length ? "attached" : "");
}

// Mirror of the backend validity rules so the counter updates live.
function scirAnswered(it, a) {
    a = a || {};
    if (it.type === "attach") {
        const st = effStatus(a);
        if (st === "attached") return (a.attachments || []).length > 0;
        if (st === "not_applicable" || st === "not_available") return !!String(a.reason || "").trim();
        if (st === "already_provided") { const pr = a.provided_ref || {}; return !!(pr.submission_id || String(pr.name || "").trim()); }
        return false;
    }
    if (it.type === "yesno") return a.value === "Yes" || a.value === "No";
    return !!String(a.value || "").trim();
}

export function PortalInfoList() {
    const [subs, setSubs] = useState(null);
    const [types, setTypes] = useState([]);
    const [creating, setCreating] = useState(false);
    const nav = useNavigate();
    const load = () => api.get("/portal/scir").then((r) => setSubs(r.data)).catch((e) => toast.error(fmtErr(e.response?.data?.detail)));
    useEffect(() => { load(); api.get("/portal/scir/job-types").then((r) => setTypes(r.data)).catch(() => {}); }, []);
    return (
        <PortalLayout>
            <Link to="/portal" className="text-xs text-brand hover:underline flex items-center gap-1 mb-3"><ChevronLeft className="w-3.5 h-3.5" /> Back</Link>
            <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-display mb-1">Send us your year-end information</div>
                    <h1 className="font-display font-bold text-3xl tracking-tight">Service charge information</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">Upload everything we need to prepare, audit or report on your service charge accounts — any time, no need to wait for our email. You can save and come back later.</p>
                </div>
                <button data-testid="scir-new-btn" onClick={() => setCreating(true)} className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-2.5 rounded-sm"><Plus className="w-4 h-4" /> Start a new submission</button>
            </div>

            {creating && <NewSubmission types={types} onCancel={() => setCreating(false)} onCreated={(id) => nav(`/portal/info/${id}`)} />}

            {subs === null ? <div className="text-muted-foreground text-sm">Loading…</div> : subs.length === 0 ? (
                <div className="bg-white border border-border rounded-sm p-8 text-sm text-muted-foreground">No information submitted yet. Click <b>Start a new submission</b> to begin.</div>
            ) : (
                <div className="grid gap-3" data-testid="scir-submission-list">
                    {subs.map((s) => (
                        <Link key={s.id} to={`/portal/info/${s.id}`} data-testid={`scir-sub-${s.id}`} className="bg-white border border-border rounded-sm p-5 flex items-center gap-6 hover:border-brand transition-colors">
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-bold text-lg truncate">{s.property_name || "(untitled property)"}</div>
                                <div className="text-xs text-muted-foreground font-num">{s.label}{s.period_end ? ` · Year end ${s.period_end.slice(0, 10)}` : ""}</div>
                            </div>
                            {(() => { const c = CLIENT_STATUS[clientStatusKey(s)]; const I = c.icon; return (
                                <div className={`text-xs font-semibold px-2.5 py-1 rounded-sm inline-flex items-center gap-1.5 ${c.cls}`}>
                                    <I className="w-3.5 h-3.5" />{c.label}
                                </div>
                            ); })()}
                        </Link>
                    ))}
                </div>
            )}
        </PortalLayout>
    );
}

function NewSubmission({ types, onCancel, onCreated }) {
    const [form, setForm] = useState({ job_type: "", property_name: "", period_end: "" });
    const create = async () => {
        if (!form.job_type) return toast.error("Choose the type of work");
        try { const { data } = await api.post("/portal/scir", form); toast.success("Draft created"); onCreated(data.id); }
        catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };
    return (
        <div className="bg-white border-2 border-brand/30 rounded-sm p-5 mb-5" data-testid="scir-new-form">
            <div className="font-display font-bold mb-3">New submission</div>
            <div className="grid sm:grid-cols-3 gap-3">
                <label className="text-xs">Type of work
                    <select data-testid="scir-new-jobtype" value={form.job_type} onChange={(e) => setForm({ ...form, job_type: e.target.value })} className="w-full mt-1 border border-input rounded-sm px-2 py-2 text-sm bg-white">
                        <option value="">Select…</option>
                        {types.map((t) => <option key={t.job_type} value={t.job_type}>{t.label}</option>)}
                    </select>
                </label>
                <label className="text-xs">Property / scheme name
                    <input data-testid="scir-new-property" value={form.property_name} onChange={(e) => setForm({ ...form, property_name: e.target.value })} className="w-full mt-1 border border-input rounded-sm px-2 py-2 text-sm" placeholder="e.g. Acacia Court" />
                </label>
                <label className="text-xs">Year end (optional)
                    <input data-testid="scir-new-period" type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="w-full mt-1 border border-input rounded-sm px-2 py-2 text-sm" />
                </label>
            </div>
            <div className="flex gap-2 mt-4">
                <button data-testid="scir-new-create" onClick={create} className="bg-brand hover:bg-brand-hover text-white text-sm px-4 py-2 rounded-sm">Create & start</button>
                <button onClick={onCancel} className="text-sm border border-border px-4 py-2 rounded-sm hover:bg-slate-50">Cancel</button>
            </div>
        </div>
    );
}

export function PortalInfoForm() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [answers, setAnswers] = useState({});
    const [providedDocs, setProvidedDocs] = useState([]);
    const [saving, setSaving] = useState(false);
    const [creply, setCreply] = useState("");
    const [csending, setCsending] = useState(false);
    const saveTimer = useRef(null);
    const lastToast = useRef(0);
    const load = () => api.get(`/portal/scir/${id}`).then((r) => { setData(r.data); setAnswers(r.data.submission.answers || {}); }).catch((e) => toast.error(fmtErr(e.response?.data?.detail)));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
    useEffect(() => { api.get(`/portal/scir/provided-docs?exclude=${id}`).then((r) => setProvidedDocs(r.data)).catch(() => {}); }, [id]);
    if (!data) return <PortalLayout><div className="text-sm text-muted-foreground">Loading…</div></PortalLayout>;
    const { submission: s, template } = data;
    const reopened = s.status !== "draft" && s.client_status === "needs_info" && s.can_edit;
    const readOnly = !(s.can_edit && (s.status === "draft" || s.client_status === "needs_info"));
    const items = template.sections.flatMap((sec) => sec.items).filter((it) => it.type !== "statement");
    const isAnswered = (it) => scirAnswered(it, answers[it.id]);
    const required = items.filter((it) => it.required);
    const doneCount = required.filter(isAnswered).length;
    const missing = required.filter((it) => !isAnswered(it));

    const goToItem = (itId) => {
        const el = document.getElementById(`scir-item-${itId}`);
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-amber-400"); setTimeout(() => el.classList.remove("ring-2", "ring-amber-400"), 1600); }
    };

    const persist = (next) => {
        if (readOnly) return;
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            setSaving(true);
            api.put(`/portal/scir/${id}`, { answers: next })
                .then(() => { const now = Date.now(); if (now - lastToast.current > 4000) { lastToast.current = now; toast.success("Progress saved automatically"); } })
                .finally(() => setSaving(false));
        }, 700);
    };
    const flushSave = async () => {
        clearTimeout(saveTimer.current);
        if (readOnly) return;
        setSaving(true);
        try { await api.put(`/portal/scir/${id}`, { answers }); } finally { setSaving(false); }
    };
    const saveAndBack = async () => {
        await flushSave();
        if (!readOnly) toast.success("Progress saved — you can come back and finish any time");
        nav("/portal/info");
    };
    const setAns = (itId, patch) => { const next = { ...answers, [itId]: { ...answers[itId], ...patch } }; setAnswers(next); persist(next); };

    const submit = async () => {
        try { await api.post(`/portal/scir/${id}/submit`); toast.success("Sent to Qube — thank you!"); nav("/portal/info"); }
        catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "object" ? d.message : fmtErr(d));
        }
    };

    const sendReply = async () => {
        const text = creply.trim();
        if (!text) return;
        setCsending(true);
        try { await api.post(`/portal/scir/${id}/respond`, { text }); setCreply(""); toast.success("Sent to Qube"); load(); }
        catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
        finally { setCsending(false); }
    };
    const responses = s.responses || [];

    return (
        <PortalLayout>
            <button onClick={saveAndBack} data-testid="scir-back-all" className="text-xs text-brand hover:underline flex items-center gap-1 mb-3"><ChevronLeft className="w-3.5 h-3.5" /> All submissions{!readOnly && <span className="text-muted-foreground">· progress is saved</span>}</button>
            <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-display mb-1">{template.label}{s.period_end ? ` · Year end ${s.period_end.slice(0, 10)}` : ""}</div>
                    <h1 className="font-display font-bold text-3xl tracking-tight">{s.property_name || "Service charge information"}</h1>
                </div>
            </div>

            {user?.ma_role === "manager" && <ManagerAllocate sid={id} />}

            {s.status !== "draft" && (() => { const k = clientStatusKey(s); const c = CLIENT_STATUS[k]; return (
                <div className={`border text-sm rounded-sm px-4 py-3 mb-5 flex items-center gap-2 ${k === "needs_info" ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`} data-testid="scir-submitted-banner">
                    {k === "needs_info" ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                    {k === "needs_info" ? (reopened ? "Qube needs more information — update your answers below and resend." : "Qube needs more information — please add a reply below.") : `This information has been sent to Qube. Status: ${c.label}.`}
                </div>
            ); })()}

            {s.status !== "draft" && (
                <div className="bg-white border border-border rounded-sm p-4 mb-6" data-testid="portal-scir-thread">
                    <div className="flex items-center gap-1.5 font-display font-bold mb-3"><Send className="w-4 h-4 text-brand" /> Messages with Qube</div>
                    {responses.length === 0 ? (
                        <div className="text-xs text-muted-foreground mb-3">No messages yet. If Qube needs anything further, you'll see it here and get an email.</div>
                    ) : (
                        <div className="space-y-2 mb-3">
                            {responses.map((r) => (
                                <div key={r.id} className={`text-sm rounded-sm px-3 py-2 ${r.author_role === "Qube" ? "bg-brand/5 border border-brand/20" : "bg-slate-50 border border-border"}`} data-testid={`portal-scir-msg-${r.id}`}>
                                    <div className="text-[11px] text-muted-foreground mb-0.5">{r.author_role === "Qube" ? "Qube Accountants" : r.author} · {(r.at || "").slice(0, 16).replace("T", " ")}</div>
                                    <div className="whitespace-pre-wrap">{r.text}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex gap-2 items-start">
                        <textarea data-testid="portal-scir-reply-text" value={creply} onChange={(e) => setCreply(e.target.value)} rows={2} placeholder="Reply to Qube…" className="flex-1 border border-input rounded-sm px-2 py-1.5 text-sm" />
                        <button data-testid="portal-scir-reply-send" disabled={csending || !creply.trim()} onClick={sendReply} className="flex items-center gap-1.5 bg-brand hover:bg-brand-hover text-white text-sm px-4 py-2 rounded-sm disabled:opacity-40"><Send className="w-3.5 h-3.5" /> Send</button>
                    </div>
                </div>
            )}

            {!readOnly && (
                <div className="sticky top-0 z-30 bg-white border border-border rounded-sm p-4 mb-6 shadow-md" data-testid="scir-progress-panel">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-sm font-medium" data-testid="scir-progress-count">{doneCount} of {required.length} questions completed</div>
                        <div className={`text-xs flex items-center gap-1 font-medium ${saving ? "text-muted-foreground" : "text-emerald-700"}`} data-testid="scir-save-state">
                            {saving ? <><Clock className="w-3.5 h-3.5" /> Saving…</> : <><CheckCircle2 className="w-3.5 h-3.5" /> All changes saved</>}
                        </div>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-2">
                        <div className="h-full bg-brand transition-all" style={{ width: `${required.length ? Math.round((doneCount / required.length) * 100) : 0}%` }} />
                    </div>
                    {missing.length > 0 && (
                        <div className="mt-3" data-testid="scir-missing-list">
                            <div className="text-xs text-muted-foreground mb-1.5">Still to answer — click to jump to a question:</div>
                            <div className="flex flex-wrap gap-1.5">
                                {missing.map((it) => (
                                    <button key={it.id} data-testid={`scir-missing-${it.id}`} onClick={() => goToItem(it.id)} className="text-[11px] border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 rounded-full px-2.5 py-0.5 text-left">{it.label.length > 48 ? it.label.slice(0, 48) + "…" : it.label}</button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {template.sections.map((sec, si) => (
                <div key={si} className="mb-6">
                    <div className="font-display font-bold text-lg">{sec.title}</div>
                    {sec.help && <p className="text-xs text-muted-foreground mb-3 mt-0.5">{sec.help}</p>}
                    <div className="space-y-3">
                        {sec.items.map((it) => it.type === "statement" ? (
                            <div key={it.id} className="text-sm text-muted-foreground bg-slate-50 border border-border rounded-sm p-3">{it.label}</div>
                        ) : (
                            <ItemRow key={it.id} it={it} a={answers[it.id] || {}} readOnly={readOnly} answered={isAnswered(it)} providedDocs={providedDocs} onChange={(patch) => setAns(it.id, patch)} />
                        ))}
                    </div>
                </div>
            ))}

            {!readOnly && (
                <div className="sticky bottom-0 bg-[#f4f5f2] border-t border-border py-4 flex items-center gap-4 flex-wrap">
                    <button data-testid="scir-submit-btn" disabled={missing.length > 0} onClick={submit} className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-sm font-medium px-5 py-2.5 rounded-sm disabled:opacity-40">
                        <Send className="w-4 h-4" /> {reopened ? "Resend to Qube" : "Submit to Qube"}
                    </button>
                    {missing.length > 0 ? (
                        <span className="text-xs text-amber-700" data-testid="scir-missing-count">{missing.length} question{missing.length > 1 ? "s" : ""} still to answer before you can submit</span>
                    ) : (
                        <span className="text-xs text-emerald-700">All done — ready to submit</span>
                    )}
                </div>
            )}
        </PortalLayout>
    );
}

const QUBE_HELP_EMAIL = "accounts@qubeaccountants.co.uk";
const MAX_BYTES = 250 * 1024 * 1024;

function ManagerAllocate({ sid }) {
    const [open, setOpen] = useState(false);
    const [cols, setCols] = useState(null);
    const [sel, setSel] = useState(new Set());
    const load = () => api.get(`/portal/scir/${sid}/colleagues`).then((r) => { setCols(r.data); setSel(new Set(r.data.filter((c) => c.allocated).map((c) => c.id))); }).catch((e) => toast.error(fmtErr(e.response?.data?.detail)));
    const toggle = (cid) => setSel((prev) => { const n = new Set(prev); n.has(cid) ? n.delete(cid) : n.add(cid); return n; });
    const save = async () => { try { await api.post(`/portal/scir/${sid}/allocate`, { user_ids: [...sel] }); toast.success("Colleague access updated"); load(); } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } };
    return (
        <div className="bg-indigo-50/60 border border-indigo-200 rounded-sm p-4 mb-6" data-testid="manager-allocate">
            <button onClick={() => { if (open) { setOpen(false); } else { setOpen(true); load(); } }} className="flex items-center gap-1.5 text-sm font-display font-bold text-indigo-800" data-testid="manager-allocate-toggle"><Users className="w-4 h-4" /> Allocate colleagues to this submission</button>
            {open && (cols === null ? <div className="text-xs text-muted-foreground mt-2">Loading…</div> : (
                <div className="mt-3 space-y-1.5">
                    <p className="text-xs text-muted-foreground">Allocated colleagues in your organisation can upload and answer on this submission.</p>
                    {cols.length === 0 && <div className="text-xs text-muted-foreground">No colleagues in your organisation yet.</div>}
                    {cols.map((c) => (
                        <label key={c.id} className="flex items-center gap-2 text-sm" data-testid={`manager-col-${c.id}`}>
                            <input type="checkbox" checked={sel.has(c.id)} disabled={c.is_creator} onChange={() => toggle(c.id)} data-testid={`manager-col-cb-${c.id}`} />
                            <span>{c.name} <span className="text-xs text-muted-foreground">{c.email}</span></span>
                            {c.is_manager && <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 rounded-sm">Manager</span>}
                            {c.is_creator && <span className="text-[10px] text-muted-foreground">(creator — always has access)</span>}
                            {!c.activated && <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 rounded-sm">Invited</span>}
                        </label>
                    ))}
                    <button onClick={save} data-testid="manager-allocate-save" className="mt-2 bg-indigo-700 hover:bg-indigo-800 text-white text-xs px-4 py-1.5 rounded-sm">Save access</button>
                </div>
            ))}
        </div>
    );
}

const RO_STATUS = { attached: "Attached", not_applicable: "Not applicable", not_available: "Not available yet", already_provided: "Already provided" };

function ItemRow({ it, a, readOnly, answered, providedDocs, onChange }) {
    const [queue, setQueue] = useState([]); // transient per-file upload status
    const atts = a.attachments || [];
    const setQ = (tmpId, patch) => setQueue((prev) => prev.map((q) => (q.tmpId === tmpId ? { ...q, ...patch } : q)));
    const dismiss = (tmpId) => setQueue((prev) => prev.filter((q) => q.tmpId !== tmpId));
    const errText = (err) => (err?.response?.data?.detail ? fmtErr(err.response.data.detail) : (err?.message || "Upload failed"));

    const onFiles = async (e) => {
        const files = Array.from(e.target.files || []); e.target.value = "";
        let base = a.attachments || [];
        for (const file of files) {
            const tmpId = crypto.randomUUID();
            if (file.size > MAX_BYTES) { setQueue((p) => [...p, { tmpId, name: file.name, status: "oversize" }]); continue; }
            setQueue((p) => [...p, { tmpId, name: file.name, file, status: "uploading", pct: 0 }]);
            try {
                const att = await uploadFile(file, "scir", it.id, (pc) => setQ(tmpId, { pct: pc }));
                base = [...base, att];
                onChange({ attachments: base, status: "attached" });
                dismiss(tmpId);
            } catch (err) { setQ(tmpId, { status: "failed", error: errText(err) }); }
        }
    };
    const retryOne = async (q) => {
        setQ(q.tmpId, { status: "uploading", pct: 0 });
        try {
            const att = await uploadFile(q.file, "scir", it.id, (pc) => setQ(q.tmpId, { pct: pc }));
            onChange({ attachments: [...(a.attachments || []), att], status: "attached" });
            dismiss(q.tmpId);
        } catch (err) { setQ(q.tmpId, { status: "failed", error: errText(q.err || err) }); }
    };
    const busy = queue.some((q) => q.status === "uploading");
    const removeAtt = (att) => { deleteUpload(att.id); onChange({ attachments: atts.filter((x) => x.id !== att.id) }); };

    const isAttach = it.type === "attach";
    const st = effStatus(a);
    const pref = a.provided_ref || {};

    const Uploader = (
        <div className="mt-2">
            <div className="flex gap-1.5 mb-1.5 flex-wrap">
                <textarea data-testid={`scir-note-${it.id}`} disabled={readOnly} value={a.value || ""} onChange={(e) => onChange({ value: e.target.value })} rows={1} placeholder="Add a note (optional)" className="w-full border border-input rounded-sm px-2 py-1.5 text-sm disabled:bg-slate-50" />
            </div>
            {atts.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                    {atts.map((x) => (
                        <span key={x.id} className="inline-flex items-center gap-1 text-[11px] bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-sm px-2 py-0.5" data-testid={`scir-att-${it.id}`}>
                            <CheckCircle2 className="w-3 h-3" /> {x.name || x.label}
                            {!readOnly && <button onClick={() => removeAtt(x)} className="text-emerald-700/70 hover:text-destructive" data-testid={`scir-att-remove-${it.id}`}><X className="w-3 h-3" /></button>}
                        </span>
                    ))}
                </div>
            )}
            {queue.length > 0 && (
                <div className="space-y-1.5 mb-1.5" data-testid={`scir-queue-${it.id}`}>
                    {queue.map((q) => (
                        <div key={q.tmpId} className={`text-[11px] rounded-sm px-2 py-1 border flex items-center gap-2 ${q.status === "failed" || q.status === "oversize" ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-slate-50 border-border text-slate-700"}`} data-testid={`scir-queue-item-${it.id}`}>
                            <Paperclip className="w-3 h-3 shrink-0" />
                            <span className="truncate flex-1">{q.name}</span>
                            {q.status === "uploading" && (<span className="flex items-center gap-1.5 shrink-0"><span className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden"><span className="block h-full bg-brand" style={{ width: `${q.pct || 0}%` }} /></span>{q.pct || 0}%</span>)}
                            {q.status === "failed" && (<><span className="shrink-0">Failed</span><button onClick={() => retryOne(q)} data-testid="scir-retry" className="shrink-0 underline hover:no-underline">Retry</button><button onClick={() => dismiss(q.tmpId)} className="shrink-0"><X className="w-3 h-3" /></button></>)}
                            {q.status === "oversize" && (<><span className="shrink-0">Over 250 MB</span><a href={`mailto:${QUBE_HELP_EMAIL}?subject=${encodeURIComponent("Large service charge file")}`} className="shrink-0 underline hover:no-underline" data-testid="scir-oversize-help">Email Qube</a><button onClick={() => dismiss(q.tmpId)} className="shrink-0"><X className="w-3 h-3" /></button></>)}
                        </div>
                    ))}
                </div>
            )}
            {!readOnly && (
                <>
                    <label className="inline-flex items-center gap-1.5 text-[11px] text-brand border border-brand/30 hover:bg-brand/5 rounded-sm px-2.5 py-1 cursor-pointer" data-testid={`scir-upload-${it.id}`}>
                        <UploadCloud className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Attach file(s) — PDF, Excel, Word, image or .zip"}
                        <input type="file" multiple className="hidden" onChange={onFiles} disabled={busy} />
                    </label>
                    <div className="text-[10px] text-muted-foreground mt-1">Up to 250 MB per file · multiple files allowed · <a href={`mailto:${QUBE_HELP_EMAIL}?subject=${encodeURIComponent("Large service charge file")}`} className="underline hover:no-underline">larger files? email Qube</a></div>
                </>
            )}
            {readOnly && atts.length > 0 && <AttachmentList items={atts} />}
        </div>
    );

    return (
        <div id={`scir-item-${it.id}`} className={`bg-white border rounded-sm p-4 ${answered ? "border-emerald-200" : "border-border"}`} data-testid={`scir-item-${it.id}`}>
            <div className="flex items-start gap-2">
                <div className="flex-1">
                    <div className="text-sm font-medium">{it.label} {it.required && <span className="text-rose-500">*</span>}</div>
                    {it.help && <div className="text-[11px] text-muted-foreground mt-0.5">{it.help}</div>}
                </div>
                {answered && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />}
            </div>

            {isAttach ? (
                <div className="mt-2">
                    {!readOnly ? (
                        <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`scir-docstatus-${it.id}`}>
                            {DOC_STATUS.map((o) => (
                                <button key={o.key} data-testid={`scir-docstatus-${it.id}-${o.key}`} onClick={() => onChange({ status: o.key })} className={`text-xs px-3 py-1.5 rounded-sm border ${st === o.key ? "bg-brand text-white border-brand" : "border-border hover:bg-slate-50"}`}>{o.label}</button>
                            ))}
                        </div>
                    ) : (
                        <div className="text-xs font-medium text-slate-600 mb-1" data-testid={`scir-docstatus-ro-${it.id}`}>{RO_STATUS[st] || "—"}</div>
                    )}

                    {st === "attached" && Uploader}

                    {st === "not_applicable" && (
                        <textarea data-testid={`scir-reason-${it.id}`} disabled={readOnly} value={a.reason || ""} onChange={(e) => onChange({ reason: e.target.value })} rows={2} placeholder="Tell us why this isn't applicable…" className="w-full border border-input rounded-sm px-2 py-1.5 text-sm disabled:bg-slate-50" />
                    )}

                    {st === "not_available" && (
                        <div className="space-y-2">
                            <textarea data-testid={`scir-reason-${it.id}`} disabled={readOnly} value={a.reason || ""} onChange={(e) => onChange({ reason: e.target.value })} rows={2} placeholder="Tell us why it isn't available yet…" className="w-full border border-input rounded-sm px-2 py-1.5 text-sm disabled:bg-slate-50" />
                            <label className="text-xs text-muted-foreground flex items-center gap-2">Expected by (optional)
                                <input data-testid={`scir-expected-${it.id}`} type="date" disabled={readOnly} value={(a.expected_date || "").slice(0, 10)} onChange={(e) => onChange({ expected_date: e.target.value })} className="border border-input rounded-sm px-2 py-1 text-sm disabled:bg-slate-50" />
                            </label>
                        </div>
                    )}

                    {st === "already_provided" && (
                        <div className="space-y-2">
                            {providedDocs.length > 0 ? (
                                <select data-testid={`scir-provided-${it.id}`} disabled={readOnly}
                                    value={pref.submission_id ? `${pref.submission_id}|${pref.attachment_id || ""}|${pref.name || ""}` : ""}
                                    onChange={(e) => { const [sid, aid, ...nm] = e.target.value.split("|"); onChange({ provided_ref: e.target.value ? { submission_id: sid, attachment_id: aid, name: nm.join("|") } : {} }); }}
                                    className="w-full border border-input rounded-sm px-2 py-1.5 text-sm bg-white disabled:bg-slate-50">
                                    <option value="">Select a document you've already sent us…</option>
                                    {providedDocs.map((g) => (
                                        <optgroup key={g.submission_id} label={`${g.property_name} · ${g.label}${g.submitted_at ? ` · ${g.submitted_at.slice(0, 10)}` : ""}`}>
                                            {g.attachments.map((x) => <option key={x.id} value={`${g.submission_id}|${x.id || ""}|${x.name}`}>{x.name}</option>)}
                                        </optgroup>
                                    ))}
                                </select>
                            ) : (
                                <div className="text-[11px] text-muted-foreground">We couldn't find earlier documents on your portal — tell us where you provided it below.</div>
                            )}
                            <input data-testid={`scir-provided-note-${it.id}`} disabled={readOnly} value={providedDocs.length > 0 ? "" : (pref.name || "")} onChange={(e) => onChange({ provided_ref: { name: e.target.value } })} placeholder={providedDocs.length > 0 ? "Or describe where it was provided (optional)" : "e.g. emailed to Sharifa on 3 March, or in last year's submission"} className={`w-full border border-input rounded-sm px-2 py-1.5 text-sm disabled:bg-slate-50 ${providedDocs.length > 0 ? "hidden" : ""}`} />
                        </div>
                    )}
                </div>
            ) : (
                <>
                    {it.type === "text" && (
                        <div className="mt-2">
                            {it.quick?.length > 0 && !readOnly && (
                                <div className="flex gap-1.5 mb-1.5 flex-wrap">
                                    {it.quick.map((q) => <button key={q} data-testid={`scir-quick-${it.id}-${q}`} onClick={() => onChange({ value: q })} className="text-[11px] border border-brand/30 text-brand hover:bg-brand/5 rounded-full px-2.5 py-0.5">{q}</button>)}
                                </div>
                            )}
                            <textarea data-testid={`scir-text-${it.id}`} disabled={readOnly} value={a.value || ""} onChange={(e) => onChange({ value: e.target.value })} rows={2} placeholder="Type your answer…" className="w-full border border-input rounded-sm px-2 py-1.5 text-sm disabled:bg-slate-50" />
                        </div>
                    )}
                    {it.type === "yesno" && (
                        <div className="flex gap-2 mt-2">
                            {["Yes", "No"].map((v) => <button key={v} data-testid={`scir-yesno-${it.id}-${v}`} disabled={readOnly} onClick={() => onChange({ value: v })} className={`text-sm px-4 py-1.5 rounded-sm border ${a.value === v ? "bg-brand text-white border-brand" : "border-border hover:bg-slate-50"} disabled:opacity-60`}>{v}</button>)}
                        </div>
                    )}
                    {it.type === "choice" && (
                        <select data-testid={`scir-choice-${it.id}`} disabled={readOnly} value={a.value || ""} onChange={(e) => onChange({ value: e.target.value })} className="mt-2 border border-input rounded-sm px-2 py-1.5 text-sm bg-white disabled:bg-slate-50">
                            <option value="">Select…</option>
                            {it.options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                    )}

                    {it.allow_attach && (
                        <div className="mt-2">
                            {atts.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-1.5">
                                    {atts.map((x) => (
                                        <span key={x.id} className="inline-flex items-center gap-1 text-[11px] bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-sm px-2 py-0.5" data-testid={`scir-att-${it.id}`}>
                                            <CheckCircle2 className="w-3 h-3" /> {x.name || x.label}
                                            {!readOnly && <button onClick={() => removeAtt(x)} className="text-emerald-700/70 hover:text-destructive" data-testid={`scir-att-remove-${it.id}`}><X className="w-3 h-3" /></button>}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {queue.length > 0 && (
                                <div className="space-y-1.5 mb-1.5" data-testid={`scir-queue-${it.id}`}>
                                    {queue.map((q) => (
                                        <div key={q.tmpId} className={`text-[11px] rounded-sm px-2 py-1 border flex items-center gap-2 ${q.status === "failed" || q.status === "oversize" ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-slate-50 border-border text-slate-700"}`} data-testid={`scir-queue-item-${it.id}`}>
                                            <Paperclip className="w-3 h-3 shrink-0" />
                                            <span className="truncate flex-1">{q.name}</span>
                                            {q.status === "uploading" && (<span className="flex items-center gap-1.5 shrink-0"><span className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden"><span className="block h-full bg-brand" style={{ width: `${q.pct || 0}%` }} /></span>{q.pct || 0}%</span>)}
                                            {q.status === "failed" && (<><span className="shrink-0">Failed</span><button onClick={() => retryOne(q)} data-testid="scir-retry" className="shrink-0 underline hover:no-underline">Retry</button><button onClick={() => dismiss(q.tmpId)} className="shrink-0"><X className="w-3 h-3" /></button></>)}
                                            {q.status === "oversize" && (<><span className="shrink-0">Over 250 MB</span><a href={`mailto:${QUBE_HELP_EMAIL}?subject=${encodeURIComponent("Large service charge file")}`} className="shrink-0 underline hover:no-underline" data-testid="scir-oversize-help">Email Qube</a><button onClick={() => dismiss(q.tmpId)} className="shrink-0"><X className="w-3 h-3" /></button></>)}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {!readOnly && (
                                <label className="inline-flex items-center gap-1.5 text-[11px] text-brand border border-brand/30 hover:bg-brand/5 rounded-sm px-2.5 py-1 cursor-pointer" data-testid={`scir-upload-${it.id}`}>
                                    <UploadCloud className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Attach file(s) (optional)"}
                                    <input type="file" multiple className="hidden" onChange={onFiles} disabled={busy} />
                                </label>
                            )}
                            {readOnly && atts.length > 0 && <AttachmentList items={atts} />}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
